import React from 'react';
import {
  Wrapper,
  Overlay,
  Container,
  Header,
  BackButton,
  TitleContainer,
  Title,
  MarkAsRead,
  Notifications,
  ScrollableContainer,
} from './style';
import NotificationList from 'components/canvas/components/notification-side-drawer/components/notification-list';
import useCurrentSurface from 'routes/surface/hooks/use-current-surface';
import useNotificationSideDrawerVisibility from 'components/canvas/hooks/use-notification-side-drawer-visibility';
import { useTranslation } from 'i18n';
import markAllNotificationsAsSeen from 'graphql/user/mutations/mark-all-notifications-as-seen';
import toast from 'services/toast';
import apolloClient from 'services/apollo-client';
import { notificationsQuery } from 'graphql/user/queries/notifications';

function NotificationSideDrawer() {
  const { t } = useTranslation();

  const surface = useCurrentSurface();

  const { isVisible, setVisibility } = useNotificationSideDrawerVisibility();

  const { hasNotifications } = useUnseenNotificationsFromCache();

  async function markAllAsSeen() {
    const result = await markAllNotificationsAsSeen();

    if (result.isFailure) {
      return toast.error(
        t('Something went wrong, could not complete the operation'),
      );
    }
  }

  function closeDrawer() {
    setVisibility(false);
  }

  if (isVisible === false) {
    return null;
  }

  return (
    <Wrapper isVisible={isVisible}>
      <Overlay onClick={closeDrawer} />
      <Container>
        <Header>
          <BackButton onClick={closeDrawer}>
            {surface?.name
              ? t('Back to {{surface}}', {
                  replace: { surface: surface?.name },
                })
              : t('Back')}
          </BackButton>
          <TitleContainer>
            <Title>{t('Notifications')}</Title>
            {hasNotifications && (
              <MarkAsRead onClick={markAllAsSeen}>
                {t('Mark all as read')}
              </MarkAsRead>
            )}
          </TitleContainer>
        </Header>
        <Notifications>
          <ScrollableContainer>
            <NotificationList />
          </ScrollableContainer>
        </Notifications>
      </Container>
    </Wrapper>
  );
}

function useUnseenNotificationsFromCache(): {
  totalCount: number;
  hasNotifications: boolean;
} {
  const { notifications } =
    apolloClient.readQuery({
      query: notificationsQuery,
      variables: { input: { filter: { status: 'unseen' } } },
    }) || {};

  const totalCount = notifications?.meta?.totalCount ?? 0;

  return { totalCount, hasNotifications: Boolean(totalCount) };
}

export default NotificationSideDrawer;
